import TrackIcon from "@material-ui/icons/Audiotrack"
import BadgeIcon from "@material-ui/icons/Cake"
import DynamicFeedIcon from "@material-ui/icons/DynamicFeed"
import EmailIcon from "@material-ui/icons/Email"
import EventIcon from "@material-ui/icons/Event"
import CommunityIcon from "@material-ui/icons/Group"
import PromocodeIcon from "@material-ui/icons/LocalOffer"
import UserIcon from "@material-ui/icons/Person"
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart"
import StoreIcon from "@material-ui/icons/Store"
import MassMessageIcon from "@material-ui/icons/Telegram"
import { useDataProvider } from "@ra-data-prisma/dataprovider"
import { FC } from "react"
import { Admin, AuthProvider, Resource } from "react-admin"

import {
  ClaimedBadgeCreate,
  ClaimedBadgeEdit,
  ClaimedBadgeList,
  ClaimedBadgeShow,
} from "./badge-claimed"
import {
  TemplateBadgeCreate,
  TemplateBadgeEdit,
  TemplateBadgeList,
  TemplateBadgeShow,
} from "./badge-template"
import { CommunityCreate, CommunityEdit, CommunityList, CommunityShow } from "./community"
import { EmailCampaignCreate, EmailCampaignEdit, EmailCampaignList } from "./email-campaign"
import { EventCreate, EventEdit, EventList } from "./event"
import {
  EventCategoryCreate,
  EventCategoryEdit,
  EventCategoryList,
  EventCategoryShow,
} from "./eventCategory"
import { FeedItemCreate, FeedItemEdit, FeedItemList } from "./feedItem"
import { MassMessageCreate, MassMessageEdit, MassMessageList } from "./mass-message"
import { ProductCreate, ProductEdit, ProductList } from "./product"
import { PromocodeCreate, PromocodeEdit, PromocodeList, PromocodeShow } from "./promocode"
import { PurchaseCreate, PurchaseEdit, PurchaseList } from "./purchase"
import { PurchaseTransferList } from "./purchaseTransfer"
import { TrackCreate, TrackEdit, TrackList } from "./track"
import { UserCreate, UserEdit, UserList } from "./user"
import { login } from "./utils/api"

const App: FC = () => {
  const authProvider: AuthProvider = {
    checkAuth: () => {
      return localStorage.getItem("token") ? Promise.resolve() : Promise.reject()
    },
    checkError: () => {
      return Promise.resolve()
    },
    getPermissions: () => {
      return Promise.resolve()
    },
    login: async ({ username, password }: { username: string; password: string }) => {
      const data = await login(username, password)
      if (data?.login?.token) {
        localStorage.setItem("token", data.login.token)
      } else {
        throw new Error("Invalid admin credentials.")
      }
      return Promise.resolve()
    },
    logout: () => {
      if (localStorage.getItem("token")) {
        localStorage.removeItem("token")
      }
      return Promise.resolve()
    },
  }

  const dataProvider = useDataProvider({
    aliasPrefix: "admin",
    clientOptions: {
      // @ra-data-prisma/dataprovider does not support headers so we send token as a query string.
      uri:
        (process.env.REACT_APP_API_URL ?? "/") +
        `?client=werz-admin&token=${localStorage.getItem("token") ?? ""}`,
    },
  })

  if (!dataProvider) {
    return <div />
  }

  return (
    <Admin authProvider={authProvider} dataProvider={dataProvider}>
      <Resource create={UserCreate} edit={UserEdit} icon={UserIcon} list={UserList} name="User" />
      <Resource
        create={CommunityCreate}
        edit={CommunityEdit}
        icon={CommunityIcon}
        list={CommunityList}
        name="Community"
        show={CommunityShow}
      />
      <Resource
        create={EventCreate}
        edit={EventEdit}
        icon={EventIcon}
        list={EventList}
        name="Event"
      />
      <Resource
        create={EventCategoryCreate}
        edit={EventCategoryEdit}
        icon={EventIcon}
        list={EventCategoryList}
        name="EventCategory"
        options={{
          label: "Event Categories",
        }}
        show={EventCategoryShow}
      />
      <Resource
        create={MassMessageCreate}
        edit={MassMessageEdit}
        icon={MassMessageIcon}
        list={MassMessageList}
        name="MassMessage"
        options={{
          label: "Mass Messages",
        }}
      />
      <Resource
        create={EmailCampaignCreate}
        edit={EmailCampaignEdit}
        icon={EmailIcon}
        list={EmailCampaignList}
        name="EmailCampaign"
        options={{
          label: "Email Campaigns",
        }}
      />
      <Resource
        create={ProductCreate}
        edit={ProductEdit}
        icon={StoreIcon}
        list={ProductList}
        name="Product"
        options={{
          label: "Products",
        }}
      />
      <Resource
        create={PromocodeCreate}
        edit={PromocodeEdit}
        icon={PromocodeIcon}
        list={PromocodeList}
        name="Promocode"
        options={{
          label: "Promocodes",
        }}
        show={PromocodeShow}
      />
      <Resource
        create={PurchaseCreate}
        edit={PurchaseEdit}
        icon={ShoppingCartIcon}
        list={PurchaseList}
        name="Purchase"
        options={{
          label: "Purchases",
        }}
      />
      <Resource
        icon={ShoppingCartIcon}
        list={PurchaseTransferList}
        name="PurchaseTransfer"
        options={{
          label: "Purchase Transfers",
        }}
      />
      <Resource
        create={FeedItemCreate}
        edit={FeedItemEdit}
        icon={DynamicFeedIcon}
        list={FeedItemList}
        name="FeedItem"
        options={{
          label: "Announcements",
        }}
      />
      <Resource
        create={TrackCreate}
        edit={TrackEdit}
        icon={TrackIcon}
        list={TrackList}
        name="Track"
        options={{
          label: "Radio Tracks",
        }}
      />
      <Resource
        create={TemplateBadgeCreate}
        edit={TemplateBadgeEdit}
        icon={BadgeIcon}
        list={TemplateBadgeList}
        name="TemplateBadge"
        options={{
          label: "Template Badges",
        }}
        show={TemplateBadgeShow}
      />
      <Resource
        create={ClaimedBadgeCreate}
        edit={ClaimedBadgeEdit}
        icon={BadgeIcon}
        list={ClaimedBadgeList}
        name="ClaimedBadge"
        options={{
          label: "Claimed Badges",
        }}
        show={ClaimedBadgeShow}
      />
    </Admin>
  )
}

export default App
